import React, { useState } from 'react';
import useAppStore from '../store/appStore';
import RegulationViewer from './RegulationViewer';
import AnalysisPanel from './AnalysisPanel';
import EmptyState from './EmptyState';
import { ChevronLeft, ChevronRight, Layers, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

const MainContent = () => {
  const {
    selectedRegulations,
    activeRegulationId,
    setActiveRegulation,
    viewMode,
    regulations,
  } = useAppStore();

  // Log component state
  React.useEffect(() => {
    console.log('%c📦 [MainContent] Component state:', 'color: #2196F3; font-weight: bold', {
      activeRegulationId,
      viewMode,
      selectedCount: selectedRegulations.length,
      totalRegulations: regulations.length
    });
  }, [activeRegulationId, viewMode, selectedRegulations, regulations]);

  const [leftPanelWidth, setLeftPanelWidth] = useState(50);
  const [viewAllRegulations, setViewAllRegulations] = useState(false);

  const activeRegulation = regulations.find(r => r.id === activeRegulationId);

  const handlePreviousRegulation = () => {
    const currentIndex = selectedRegulations.indexOf(activeRegulationId);
    if (currentIndex > 0) {
      const prevId = selectedRegulations[currentIndex - 1];
      console.log('%c⬅️ [MainContent] Navigate to previous regulation:', 'color: #9C27B0', {
        from: activeRegulationId,
        to: prevId,
        index: `${currentIndex} -> ${currentIndex - 1}`
      });
      setActiveRegulation(prevId);
    }
  };

  const handleNextRegulation = () => {
    const currentIndex = selectedRegulations.indexOf(activeRegulationId);
    if (currentIndex < selectedRegulations.length - 1) {
      const nextId = selectedRegulations[currentIndex + 1];
      console.log('%c➡️ [MainContent] Navigate to next regulation:', 'color: #9C27B0', {
        from: activeRegulationId,
        to: nextId,
        index: `${currentIndex} -> ${currentIndex + 1}`
      });
      setActiveRegulation(nextId);
    }
  };


  if (selectedRegulations.length === 0) {
    return (
      <div className="main-content" style={{ position: 'relative' }}>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="main-content">
      {/* Content Header */}
      <div className="content-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1, marginRight: '20px', overflow: 'hidden' }}>
            {selectedRegulations.length > 1 ? (
              <>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  paddingBottom: '4px'
                }}>
                  <h2 className="content-title" style={{ fontSize: '16px', flexShrink: 0 }}>
                    {selectedRegulations.map((id, index) => {
                      const reg = regulations.find(r => r.id === id);
                      return (
                        <span key={id}>
                          {index > 0 && <span style={{ margin: '0 8px', color: '#9ca3af' }}>|</span>}
                          <span 
                            style={{ 
                              fontWeight: id === activeRegulationId ? '700' : '500',
                              color: id === activeRegulationId ? '#111827' : '#6b7280',
                              cursor: 'pointer'
                            }}
                            onClick={() => setActiveRegulation(id)}
                          >
                            {reg?.number}
                          </span>
                        </span>
                      );
                    })}
                  </h2>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <p className="content-subtitle" style={{ margin: 0 }}>Multiple Regulations Selected</p>
                  <button
                    onClick={() => setViewAllRegulations(!viewAllRegulations)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: viewAllRegulations ? '#3b82f6' : '#f3f4f6',
                      color: viewAllRegulations ? 'white' : '#374151',
                      border: '1px solid ' + (viewAllRegulations ? '#3b82f6' : '#e5e7eb'),
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Eye size={14} />
                    {viewAllRegulations ? 'Hide All' : 'View All'}
                  </button>
                </div>
              </>
            ) : activeRegulation && (
              <>
                <h2 className="content-title">
                  {(() => {
                    // Handle CFR format
                    if (activeRegulation.number?.includes('_cfr_')) {
                      return activeRegulation.number.replace(/(\d+)_cfr_(\d+)/, 'CFR $1.$2');
                    }
                    
                    // Handle VAC format
                    const match = activeRegulation.number?.match(/(\d+)VAC([\d-]+)/);
                    if (match) {
                      const title = match[1];
                      const rest = match[2];
                      const sections = rest.split('-');
                      const url = `https://law.lis.virginia.gov/admincode/title${title}/agency${sections[0]}/chapter${sections[1]}/section${sections[2]}/`;
                      return (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {activeRegulation.number}
                        </a>
                      );
                    }
                    return activeRegulation.number;
                  })()}
                </h2>
                <p className="content-subtitle">{activeRegulation.title}</p>
              </>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {selectedRegulations.length > 1 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={handlePreviousRegulation}
                    disabled={selectedRegulations.indexOf(activeRegulationId) === 0}
                    className="btn btn-secondary"
                    style={{ padding: '6px' }}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-small">
                    {selectedRegulations.indexOf(activeRegulationId) + 1} of {selectedRegulations.length}
                  </span>
                  <button
                    onClick={handleNextRegulation}
                    disabled={selectedRegulations.indexOf(activeRegulationId) === selectedRegulations.length - 1}
                    className="btn btn-secondary"
                    style={{ padding: '6px' }}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Split View */}
      <div className="split-view">
        {/* Left Panel - Regulation Text */}
        <div 
          className="split-panel left"
          style={{ width: `${leftPanelWidth}%` }}
        >
          <RegulationViewer 
            regulation={activeRegulation}
            showMultiple={viewAllRegulations && selectedRegulations.length > 1}
            regulations={selectedRegulations.map(id => regulations.find(r => r.id === id)).filter(Boolean)}
          />
        </div>

        {/* Resizer */}
        <div
          style={{
            width: '1px',
            background: 'var(--border)',
            cursor: 'col-resize',
            position: 'relative',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--border)'; }}
          onMouseDown={(e) => {
            const startX = e.clientX;
            const startWidth = leftPanelWidth;
            
            const handleMouseMove = (e) => {
              const deltaX = e.clientX - startX;
              const containerWidth = document.querySelector('.split-view').offsetWidth;
              const deltaPercent = (deltaX / containerWidth) * 100;
              const newWidth = Math.max(30, Math.min(70, startWidth + deltaPercent));
              setLeftPanelWidth(newWidth);
            };
            
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        />

        {/* Right Panel - Analysis */}
        <div 
          className="split-panel"
          style={{ width: `${100 - leftPanelWidth}%` }}
        >
          <AnalysisPanel 
            regulation={activeRegulation} 
            regulations={selectedRegulations.map(id => regulations.find(r => r.id === id)).filter(Boolean)}
          />
        </div>
      </div>
    </div>
  );
};

export default MainContent;